import { useAnalytics } from "@codat/analytics";
import { useToast } from "@codat/orchard-ui";
import { useGenerateExcelDownloadMutation } from "api/mutations/useGenerateExcelDownloadMutations";
import { useGetExcelDownloadQuery } from "api/queries/useExcelDownloadQuery";
import { useGetExcelDownloadMetaDataQuery } from "api/queries/useGetExcelDownloadMetaDataQuery";
import { ApiError } from "api/shared";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";

import { extractFileName } from "../utils/extractFileName";

export const useExportModalState = (companyName: string, companyId: string) => {
  const POLL_INTERVAL = 3_000;
  const pollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const {
    metadata,
    metadataLoading,
    getMetaData,
    generateExcel,
    downloadExcel,
  } = useGenerateExcelReport(companyId);

  const handleExcelFileDownload = async () => {
    try {
      const data = await downloadExcel();
      const { data: blob, headers: obj } = data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.dataset["testid"] = "download-link";
      a.style.display = "none";
      a.href = url;
      a.download =
        extractFileName(obj["content-disposition"]) ?? `${companyName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      sendEvent("Click", {
        element: "Download Excel Report",
      });
      addToast({
        variant: "success",
        message: "Your download has started",
      });
    } catch (err) {
      addToast({
        variant: "error",
        message: "Failed to download",
      });
    }
  };

  const generateNewReport = async () => {
    setIsGeneratingReport(true);
    try {
      await generateExcel();
      await pollForLatestExcelReport();

      setIsGeneratingReport(false);
      addToast({
        variant: "success",
        message: "New report generated",
      });
    } catch (err: unknown) {
      setIsGeneratingReport(false);
      addToast({
        variant: "error",
        message: "Failed to generate new report",
        subText: (err as AxiosError<ApiError>)?.response?.data?.error,
      });
    }
  };

  const pollForLatestExcelReport = async () => {
    return new Promise((resolve, reject) => {
      (function poll() {
        getMetaData()
          .then(({ data }) => {
            if (data.inProgress) {
              pollTimeout.current = setTimeout(poll, POLL_INTERVAL);
            } else {
              if (!data.success) {
                reject(data.errorMessage);
              }
              if (pollTimeout.current) {
                clearTimeout(pollTimeout.current);
              }
              resolve(null);
            }
          })
          .catch((error) => reject(error));
      })();
    });
  };

  useEffect(() => {
    return () => {
      if (pollTimeout.current) {
        clearTimeout(pollTimeout.current);
      }
    };
  }, []);

  return {
    metadata,
    metadataLoading,
    isGeneratingReport,
    generateNewReport,
    generateExcel,
    handleExcelFileDownload,
  };
};

const useGenerateExcelReport = (companyId: string) => {
  const [{ data: metadata, loading: metadataLoading }, getMetaData] =
    useGetExcelDownloadMetaDataQuery({
      companyId,
    });

  const [, generateExcel] = useGenerateExcelDownloadMutation({
    companyId,
  });
  const [, downloadExcel] = useGetExcelDownloadQuery({
    companyId,
  });

  return {
    metadata,
    metadataLoading,
    getMetaData,
    generateExcel,
    downloadExcel,
  };
};
